import { ChangeDetectionStrategy, Component, HostListener, Input, OnChanges } from '@angular/core';
import { ScaleBand, scaleBand, ScaleLinear, scaleLinear } from 'd3-scale';
import isEqual from 'lodash/isEqual';
import { Breakpoint } from '../../utils';
import type { ChartDataEntry, ChartDataSeries, ChartDimensions } from '../common';
import { FireflyStackedBarChartComponent } from '../stacked-bar-chart';

@Component({
  selector: 'f-stacked-bar-line-chart',
  templateUrl: './stacked-bar-line-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyStackedBarLineChartComponent extends FireflyStackedBarChartComponent implements OnChanges {
  @Input() lineData!: ChartDataEntry[] | ChartDataSeries[];
  @Input() lineClasses: string[] = [];
  @Input() lineAxisLabelText!: string;
  @Input() lineAxisTickFormatting!: (d: unknown) => string;
  @Input() lineCircleRadius = 4;
  @Input() lineWidth = 2.5;
  @Input() lineCurved = false;
  @Input() showLineAxisGridlines = false;
  @Input() infoBubbleClasses: string[] = ['f-bg-bubble'];
  @Input() infoBubbleTextClasses: string[] = ['f-text-bubble', 'fw-bold'];
  @Input() infoBubbleScaleFactor = 1;
  @Input() startLineAxisDomainWithZero = true;
  @Input() totalAvailableLegendLabel?: string;
  @Input() totalAvailableLegendMarkerClass?: string;

  lineXDomain!: string[];
  lineYDomain!: number[];
  lineXScale!: ScaleBand<string>;
  lineYScale!: ScaleLinear<number, number>;
  pointerXCoordinate!: number | undefined;
  activeDataPointId = -1;

  chartComponent = FireflyStackedBarLineChartComponent;

  protected legendRootClass = 'f-stacked-bar-line-chart-legend';

  ngOnChanges() {
    super.ngOnChanges();
    this.mapLineDataToDataSeries();
    if (this.totalAvailableLegendMarkerClass && this.totalAvailableLegendLabel) {
      this.updateLegend();
    }
  }

  protected mapLineDataToDataSeries() {
    this.data = this.data
      .map((entry, index) => {
        const lineData = this.lineData[index];
        const cssClass = lineData.cssClass;
        const combinedClasses = [...this.lineClasses, cssClass].filter(Boolean).join(' ');
        if (combinedClasses) lineData.cssClass = combinedClasses;

        let newSeries = entry.series as ChartDataEntry[];

        const totalAvailable = entry.totalAvailableValue;
        if (typeof totalAvailable === 'number' && totalAvailable > 0 && this.totalAvailableLegendLabel) {
          const consumed = newSeries.reduce((acc, s) => acc + (s.value || 0), 0);

          // Filter out entire entry if totalAvailable is less than consumed
          if (totalAvailable < consumed) {
            console.warn(
              `Total available value (${totalAvailable}) is less than the consumed value (${consumed}) for entry "${entry.name}". Entry will be omitted from the chart.`
            );
            return null;
          }

          const remaining = totalAvailable - consumed;
          newSeries = [
            ...newSeries,
            {
              name: this.totalAvailableLegendLabel,
              value: remaining,
              totalAvailableValue: totalAvailable,
              cssClass: this.totalAvailableLegendMarkerClass
            }
          ];
        }
        if (this.totalAvailableLegendLabel && !totalAvailable) {
          console.warn(
            `Total available value is missing for entry "${entry.name}". Entry will be omitted from the chart.`
          );
          return null;
        }

        return { ...entry, lineData, series: newSeries };
      })
      .filter(entry => entry !== null);
  }

  protected update(dims: ChartDimensions) {
    super.update(dims);
    this.lineYScale = this.getLineYScale(dims);
    this.lineXScale = this.getLineXScale(dims);
  }

  protected updateLegend() {
    if (!this.legendOptions || !this.totalAvailableLegendLabel) return;
    const exists = this.legendOptions.data.some(d => d.label === this.totalAvailableLegendLabel);
    if (!exists) {
      this.legendOptions = {
        ...this.legendOptions,
        data: [
          ...this.legendOptions.data,
          {
            label: this.totalAvailableLegendLabel,
            markerCssClass: this.totalAvailableLegendMarkerClass ?? ''
          }
        ]
      };
    }
  }

  getLineXScale(view: ChartDimensions): ScaleBand<string> {
    this.lineXDomain = this.getXDomain(this.lineData);
    const scale = scaleBand().range([0, view.width]).padding(this.padding).domain(this.lineXDomain);
    const adjustedPadding = this.condensedView && scale.bandwidth() < this.minBarWidth ? 0 : this.padding;
    return scale.padding(adjustedPadding);
  }

  getLineYScale(view: ChartDimensions): ScaleLinear<number, number> {
    this.lineYDomain = this.getYDomain(this.lineData, this.startLineAxisDomainWithZero);
    return scaleLinear().range([view.height, 0]).domain(this.lineYDomain).nice();
  }

  onDimensionsChanged(dims: ChartDimensions) {
    if (isEqual(this.dimensions, dims)) return;

    this.dimensions = dims;
    this.xScale = this.getXScale(dims);
    this.yScale = this.getYScale(dims);
    this.lineXScale = this.getLineXScale(dims);
    this.lineYScale = this.getLineYScale(dims);
    this.cdr.detectChanges();
  }

  onBarHover({ x, index }: { x: number; index: number }) {
    this.pointerXCoordinate = x;
    if (index !== null) {
      this.activeDataPointId = index;
      this.cdr.detectChanges();
    }
    super.onBarHover({ x, index });
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (window.innerWidth < Breakpoint.Sm) return;
    this.pointerXCoordinate = undefined;
    this.activeDataPointId = -1;
    this.cdr.detectChanges();
  }
}
